import React, { useMemo, useState } from "react";
import moment from "moment";
import _ from "lodash";
import {
  Group,
  Text,
  ActionIcon,
  UnstyledButton,
  Loader,
} from "@mantine/core";
import {
  IconCirclePlus,
  IconRefresh,
} from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { useAuthUser } from "react-auth-kit";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import { useFormRender } from "../hooks/useFormRender";
import { useServerApi } from "../hooks/userServerApi";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { useFormatter } from "../hooks/useFomatter";
import DataTable from "./dataTable";
import ActionCellRender from "./actionCellRender";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { openFileUrl } from "../utils/openReport";
import { getInvestmentTransactionStatusLabel } from "../helpers/investmentTransactionStatusLabels";
import {
  resolveOrderIfastAccountNo,
  createArFormValidators,
  getArFormPrintDisabledReason,
  buildArFormValidationValues,
  formatTransactionTypesForDisplay,
} from "../helpers/arFormUtils";
import { useInvestmentTransactionSettings } from "../contexts/investmentTransactionSettingsContext";

const AddInvestmentTransactionPrompt = ({ accountNo }) => {
  const accountLabel = accountNo?.trim() || "—";

  return (
    <Text size="sm">
      Are you sure you want to create a transaction for iFAST account number (
      <Text component="span" weight={600}>
        {accountLabel}
      </Text>
      )?
    </Text>
  );
};

const OrderInvestmentTransactionList = ({ form: mainForm }) => {
  const orderId = _.get(mainForm.values, "_id");
  const transactions = _.get(mainForm.values, "investmentTransactions");
  const [rows, setRows] = useState(transactions ?? []);
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [printingId, setPrintingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const auth = useAuthUser();
  const role = auth()?.userRole?.name?.toUpperCase();
  const tabNav = useTabNavigate();
  const { t } = useTranslation();
  const { nonWrapUpfrontFeeRules } = useInvestmentTransactionSettings();

  const validators = useMemo(
    () => createArFormValidators(nonWrapUpfrontFeeRules),
    [nonWrapUpfrontFeeRules]
  );

  const getPrintDisabledReason = (transaction) =>
    getArFormPrintDisabledReason({
      transactionId: transaction?._id,
      values: buildArFormValidationValues(transaction, mainForm.values),
      validators,
    });

  useDeepCompareEffect(() => {
    setRows(transactions ?? []);
  }, [transactions]);

  const getTransactionLink = () => {
    const { userRole } = auth() ?? {};
    const roleLink = userRole?.entityLinks?.find(
      (l) => l.entity === "orderInvestmentTransaction"
    );
    if (roleLink?.link) return roleLink.link;
    if (role === "CONSULTANT") return "/consultant/orderInvestmentTransaction";
    if (role === "MANAGEMENT") return "/management/orderInvestmentTransaction";
    return "/operation/orderInvestmentTransaction";
  };

  const openTransaction = (transaction) => {
    const pathname = getTransactionLink();
    if (!pathname || !transaction?._id) return;
    tabNav.openDetail({
      pathname,
      id: transaction._id,
      mode: "edit",
      label: transaction.code,
    });
  };

  const refreshOrder = async () => {
    if (mainForm.reload) {
      await mainForm.reload();
    }
  };

  const handleRefresh = async () => {
    if (!orderId || refreshing) return;
    try {
      setRefreshing(true);
      await refreshOrder();
      const latest = _.get(mainForm.values, "investmentTransactions") ?? [];
      setRows(latest);
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Error"),
        message: error?.message ?? "Failed to refresh transactions",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const ifastAccountNo = resolveOrderIfastAccountNo({
    policyNumber: mainForm.values?.policyNumber,
    ifastAccountNo: mainForm.values?.arForm?.ifastAccountNo ?? "",
  });

  const addFormSchema = {
    name: "AddInvestmentTransactionSchema",
    title: "New Investment Transaction",
    saveWithNotification: false,
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "480px",
    },
    initialValues: {},
    saveButtonText: "Confirm",
    layout: {
      containers: [
        {
          key: "section-prompt",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            withBorder: false,
            mt: 0,
            minHeight: 0,
          },
        },
      ],
      fields: [
        {
          name: "_prompt",
          component: AddInvestmentTransactionPrompt,
          parent: "section-prompt",
          props: {
            accountNo: ifastAccountNo,
          },
        },
      ],
    },
  };

  const onAddSubmit = async () => {
    const data = await api.OrderInvestmentTransaction.createForOrder({
      orderId,
    });

    const transaction =
      data?.orderInvestmentTransaction ?? data?.doc ?? null;
    if (!transaction?._id) {
      throw new Error("Failed to create transaction");
    }

    formAction.close();
    const newRows = [...rows, transaction];
    setRows(newRows);
    mainForm.setFieldValue("investmentTransactions", newRows);
    await refreshOrder();

    if (data?.mcaIntercepted) {
      showNotification({
        color: "orange",
        title: "Submitted for approval",
        message: `${transaction.code} will be available after MCA approval.`,
      });
      return { success: true, data };
    }

    openTransaction(transaction);
    showNotification({
      title: "Transaction added",
      message: `${transaction.code} created successfully`,
    });
    return { success: true, data };
  };

  const [renderAddForm, formAction] = useFormRender(
    addFormSchema,
    null,
    onAddSubmit
  );

  const handleAddClick = () => {
    if (!orderId) return;
    formAction.open({
      mode: "add",
      data: {},
    });
  };

  const deleteTransaction = async (transaction) => {
    if (!window.confirm(`Delete transaction ${transaction.code}?`)) return;
    try {
      await api.removeById({
        apiEntity: "orderInvestmentTransaction",
        id: transaction._id,
      });
      const newRows = rows.filter((r) => r._id !== transaction._id);
      setRows(newRows);
      mainForm.setFieldValue("investmentTransactions", newRows);
      await refreshOrder();
      showNotification({
        title: "Deleted",
        message: `${transaction.code} removed`,
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.message ?? "Failed to delete transaction",
      });
    }
  };

  const printTransaction = async (transaction) => {
    if (getPrintDisabledReason(transaction)) return;

    try {
      setPrintingId(transaction._id);
      const result = await api.OrderInvestmentTransaction.printArForm(
        transaction._id,
        transaction.arForm
      );
      if (result?.url) {
        await openFileUrl(result.url);
        return;
      }
      throw new Error("No print URL returned");
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Error"),
        message: error?.message ?? "Failed to print AR Form",
      });
    } finally {
      setPrintingId(null);
    }
  };

  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "code",
        headerName: "Transaction No.",
        cellRender: (col, data) => (
          <Group spacing={4}>
            <UnstyledButton onClick={() => openTransaction(data)}>
              <Text size="sm" color="blue" weight={500}>
                {data.code}
              </Text>
            </UnstyledButton>
          </Group>
        ),
      },
      {
        field: "transactionType",
        headerName: "Transaction Type",
        cellRender: (col, data) =>
          formatTransactionTypesForDisplay(data.arForm?.transactionType),
      },
      {
        field: "investmentAmount",
        headerName: "Investment Amount",
        cellRender: (col, data) =>
          data.arForm?.investmentAmount != null
            ? formatter.currency(data.arForm.investmentAmount, "HKD")
            : "",
      },
      {
        field: "status",
        headerName: "Status",
        cellRender: (col, data) => (
          <Text size="sm">
            {getInvestmentTransactionStatusLabel(data.status, t)}
          </Text>
        ),
      },
      {
        field: "createdAt",
        headerName: "Created",
        cellRender: (col, data) =>
          data.createdAt ? moment(data.createdAt).format("YYYY-MM-DD") : "",
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [],
        actionMenu: [
          {
            name: "edit",
            label: "Edit",
          },
          {
            name: "print",
            label: "Print AR Form",
            props: {
              disabled: (row) => !!getPrintDisabledReason(row),
            },
            disabledTooltip: (row) => getPrintDisabledReason(row),
          },
          {
            name: "delete",
            label: "Delete",
            props: { color: "red" },
          },
        ],
      },
    ],
  };

  const handleActionBtnClick = ({ action, data }) => {
    switch (action) {
      case "edit":
        return openTransaction(data);
      case "print":
        return printTransaction(data);
      case "delete":
        return deleteTransaction(data);
      default:
        return null;
    }
  };

  return (
    <>
      <Group position="right" mb="sm" spacing="lg">
        <UnstyledButton
          onClick={handleRefresh}
          disabled={!orderId || refreshing}
        >
          <Group spacing={6}>
            {refreshing ? (
              <Loader size="xs" />
            ) : (
              <ActionIcon disabled={!orderId}>
                <IconRefresh size="1rem" />
              </ActionIcon>
            )}
            <Text size="xs">{t("Refresh")}</Text>
          </Group>
        </UnstyledButton>
        <UnstyledButton onClick={handleAddClick} disabled={!orderId}>
          <Group spacing={6}>
            <ActionIcon disabled={!orderId}>
              <IconCirclePlus size="1rem" />
            </ActionIcon>
            <Text size="xs">{t("Add Transaction")}</Text>
          </Group>
        </UnstyledButton>
      </Group>

      {printingId && (
        <Group spacing="xs" mb="xs">
          <Loader size="xs" />
          <Text size="xs" color="dimmed">
            Generating PDF…
          </Text>
        </Group>
      )}

      {_.isEmpty(rows) && (
        <Text size="xs" color="dimmed" mb="sm">
          {t("No transactions yet. Add one to start the AR Form.")}
        </Text>
      )}

      {!_.isEmpty(rows) && (
        <DataTable
          data={rows ?? []}
          columns={tableSchema.columns}
          hidePagination
          hideSearchBar
          onActionBtnClick={handleActionBtnClick}
        />
      )}

      {renderAddForm()}
    </>
  );
};

export default OrderInvestmentTransactionList;
